import {mxEvent, mxResources, mxUtils} from './mxGraphFactory';
import {Palette} from "./Palette";

const GRAPH_EDITOR_HELP_URL = 'https://www.simworkflow.com/pages/3UVlodSsus85SYAw8Rcyh9';

const kebabCase = str => str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map(x => x.toLowerCase())
    .join('-');

/**
 * Constructs a new toolbar for the given editor.
 */
export const Toolbar = function (editorUi, container) {
    this.editorUi = editorUi;
    this.container = container;
    this.init();

    // Global handler to hide the current menu
    this.gestureHandler = mxUtils.bind(this, function (evt) {
        if (this.editorUi.currentMenu != null && mxEvent.getSource(evt) !== this.editorUi.currentMenu.div) {
            this.hideMenu();
        }
    });

    mxEvent.addGestureListeners(document, this.gestureHandler);
};

/**
 * Adds the toolbar elements.
 */
Toolbar.prototype.init = function () {
    this.container.classList.add('geToolbarRow');
    const addSpacer = () => {
        const spacer = document.createElement('span');
        spacer.className = 'geToolbarSpacer';
        this.container.appendChild(spacer);
    };

    if (this.editorUi.editor.editable) {
        this.palette = new Palette(this.editorUi, this.container);
        addSpacer();
    }

    let group = document.createElement('div');
    group.className = 'geToolbarGroup ui basic buttons';
    let elts = this.addItems(['zoomIn', 'zoomOut', 'resetView'], group);
    elts[0].setAttribute('title', mxResources.get('zoomIn') + ' (' + this.editorUi.actions.get('zoomIn').shortcut + ')');
    elts[1].setAttribute('title', mxResources.get('zoomOut') + ' (' + this.editorUi.actions.get('zoomOut').shortcut + ')');
    elts[2].setAttribute('title', mxResources.get('resetView') + ' (' + this.editorUi.actions.get('resetView').shortcut + ')');
    this.container.appendChild(group);

    if (this.editorUi.editor.editable) {
        addSpacer();

        group = document.createElement('div');
        group.className = 'geToolbarGroup ui basic buttons';
        elts = this.addItems(['undo', 'redo'], group);
        elts[0].setAttribute('title', mxResources.get('undo') + ' (' + this.editorUi.actions.get('undo').shortcut + ')');
        elts[1].setAttribute('title', mxResources.get('redo') + ' (' + this.editorUi.actions.get('redo').shortcut + ')');
        this.container.appendChild(group);

        addSpacer();

        group = document.createElement('div');
        group.className = 'geToolbarGroup ui basic buttons';
        elts = this.addItems(['delete'], group);
        elts[0].setAttribute('title', mxResources.get('delete') + ' (' + this.editorUi.actions.get('delete').shortcut + ')');
        this.container.appendChild(group);

        addSpacer();

        group = document.createElement('div');
        group.className = 'geToolbarGroup ui basic buttons';
        this.addHelpButton(group);
        this.container.appendChild(group);
    }
};

/**
 * Hides the current menu.
 */
Toolbar.prototype.hideMenu = function () {
    this.editorUi.hideCurrentMenu();
};

/**
 * Adds a separator to the separator.
 */
Toolbar.prototype.addSeparator = function (c) {
    c = (c != null) ? c : this.container;
    const elt = document.createElement('div');
    elt.className = 'geSeparator';
    c.appendChild(elt);

    return elt;
};

/**
 * Adds given action item
 */
Toolbar.prototype.addItems = function (keys, c, ignoreDisabled) {
    const items = [];

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        if (key === '-') {
            items.push(this.addSeparator(c));
        } else {
            items.push(this.addItem(kebabCase(key), key, c, ignoreDisabled));
        }
    }

    return items;
};

/**
 * Adds given action item
 */
Toolbar.prototype.addItem = function (sprite, key, c, ignoreDisabled) {
    const action = this.editorUi.actions.get(key);
    let elt = null;

    if (action != null) {
        let tooltip = action.label;

        if (action.shortcut != null) {
            tooltip += ' (' + action.shortcut + ')';
        }

        elt = this.addButton(sprite, tooltip, action.funct, c);
        elt.setAttribute('aria-label', action.label);

        if (!ignoreDisabled) {
            elt.setEnabled(action.enabled);

            action.addListener('stateChanged', function () {
                elt.setEnabled(action.enabled);
            });
        }
    }

    return elt;
};

/**
 * Adds a button to the toolbar.
 */
Toolbar.prototype.addButton = function (className, tooltip, funct, c) {
    const elt = this.createButton(className);
    c = (c != null) ? c : this.container;

    this.initElement(elt, tooltip);
    this.addClickHandler(elt, funct);
    c.appendChild(elt);

    return elt;
};

Toolbar.prototype.addHelpButton = function (c) {
    const helpButton = this.addButton('help', mxResources.get('help'), () => {
        window.open(GRAPH_EDITOR_HELP_URL, '_blank', 'noopener,noreferrer');
    }, c);
    helpButton.setAttribute('aria-label', mxResources.get('help'));

    return helpButton;
};

/**
 * Initializes the given toolbar element.
 */
Toolbar.prototype.initElement = function (elt, tooltip) {
    // Adds tooltip
    if (tooltip != null) {
        elt.setAttribute('title', tooltip);
    }

    this.addEnabledState(elt);
};

/**
 * Adds enabled state with setter to DOM node (avoids JS wrapper).
 */
Toolbar.prototype.addEnabledState = function (elt) {
    const classname = elt.className;

    elt.setEnabled = function (value) {
        elt.enabled = value;

        if (value) {
            elt.className = classname;
        } else {
            elt.className = classname + ' disabled';
        }
    };

    elt.setEnabled(true);
};

/**
 * Adds enabled state with setter to DOM node (avoids JS wrapper).
 */
Toolbar.prototype.addClickHandler = function (elt, funct) {
    if (funct != null) {
        mxEvent.addListener(elt, 'click', function (evt) {
            if (elt.enabled) {
                funct(evt);
            }

            mxEvent.consume(evt);
        });

        if (document.documentMode != null && document.documentMode >= 9) {
            // Prevents focus
            mxEvent.addListener(elt, 'mousedown', function (evt) {
                evt.preventDefault();
            });
        }
    }
};

/**
 * Creates and returns a new button.
 */
Toolbar.prototype.createButton = function (className) {
    const elt = document.createElement('button');
    elt.className = 'ui icon button';
    elt.role = 'button';

    const inner = document.createElement('i');
    inner.setAttribute('aria-hidden', 'true');

    if (className === 'reset-view') {
        inner.className = 'search icon';
    } else if (className === 'zoom-in') {
        inner.className = 'search plus icon';
    } else if (className === 'zoom-out') {
        inner.className = 'search minus icon';
    } else if (className === 'delete') {
        inner.className = 'trash icon';
    } else if (className === 'help') {
        inner.className = 'question circle icon';
    } else if (className === 'launch') {
        inner.className = 'play icon';
    } else {
        inner.className = className + ' icon';
    }

    elt.appendChild(inner);

    return elt;
};


/**
 * Creates and returns a new button.
 */
Toolbar.prototype.createLabel = function (label) {
    const elt = document.createElement('a');
    elt.className = 'geLabel';
    mxUtils.write(elt, label);
    return elt;
};

/**
 * Adds a handler for showing a menu in the given element.
 */
Toolbar.prototype.destroy = function () {
    if (this.gestureHandler != null) {
        mxEvent.removeGestureListeners(document, this.gestureHandler);
        this.gestureHandler = null;
    }

    if (this.palette != null) {
        this.palette.destroy();
        this.palette = null;
    }
};
